import React, { useState, useEffect } from 'react';
import { Container, Card, Button, Alert } from 'react-bootstrap';
import { FaEdit, FaTrash, FaPlus, FaCheck, FaTimes } from 'react-icons/fa';
import { useAuth } from '../../../AuthContext/AuthContext';
import { fetchEmploymentData, saveEmploymentData, deleteEmploymentData, sanitizePayload, getSupervisorById } from './EmploymentActions';
import EmploymentFormSection from './EmploymentFormSection';
import { validateEmploymentFormData } from './EmploymentActions';
import { useTranslation } from 'react-i18next';
import { dutyStationService } from '../../../services/dutyStationService';

const EmploymentForm = ({ onSave, onValidationChange, progress }) => {
    const { currentUser } = useAuth();
    const [employment, setEmployment] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [isFormComplete, setIsFormComplete] = useState(false);
    const [dutyStations, setDutyStations] = useState([]);
    const [validationErrors, setValidationErrors] = useState({});
    const [touchedFields, setTouchedFields] = useState({});

    const { t } = useTranslation('employment');

    const [formData, setFormData] = useState({
        id: null,
        'function': '',
        section: '',
        unit: '',
        duty_station_id: '',
        office_location: '', // ADDED: office_location field
        type_of_contract: '',
        expiration_date: '',
        supervisor: '',
        mode_of_work: '',
        user_id: '',
    });

    // Define required fields
    const requiredFields = ['function', 'section', 'unit', 'duty_station_id', 'type_of_contract'];

    /**
     * Helper function to check if employment data object is effectively empty
     */
    const isEmptyEmploymentData = (data) => {
        if (!data) return true;

        const hasFunction = data.function && data.function.toString().trim() !== '';
        const hasSection = data.section && data.section.toString().trim() !== '';
        const hasUnit = data.unit && data.unit.toString().trim() !== '';
        const hasContract = data.type_of_contract && data.type_of_contract.toString().trim() !== '';

        return !(hasFunction || hasSection || hasUnit || hasContract);
    };

    const checkFormCompletion = (data) => {
        const isComplete = requiredFields.every(field => data[field] && data[field].toString().trim() !== '');
        return isComplete;
    };

    const validateForm = (data) => {
        const errors = validateEmploymentFormData(data);

        // Add required field validation with visual indicators
        requiredFields.forEach(field => {
            if (!data[field] || data[field].toString().trim() === '') {
                errors[field] = `${field.charAt(0).toUpperCase() + field.slice(1).replace('_', ' ')} is required`;
            }
        });

        return errors;
    };

    const normalizeForFrontend = (data) => {
        const requiredKeys = ['function', 'section', 'unit', 'type_of_contract'];
        return requiredKeys.reduce((acc, key) => {
            acc[key] = data[key]?.toString().trim() || '';
            return acc;
        }, { ...data });
    };

    // Fetch duty stations for mapping id to name
    useEffect(() => {
        const fetchDutyStations = async () => {
            try {
                const data = await dutyStationService.getDutyStationsForDropdown();
                setDutyStations(data);
            } catch (err) {
                console.error('Error fetching duty stations:', err);
                setDutyStations([]);
            }
        };
        fetchDutyStations();
    }, []);

    // Helper function to get duty station name from id
    const getDutyStationName = (id) => {
        const dutyStation = dutyStations.find(ds => ds.id === parseInt(id));
        return dutyStation ? dutyStation.name : t('form_incomplete_message');
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const data = await fetchEmploymentData();
                if (!data) {
                    setEmployment(null);
                    const initialFormData = {
                        id: null,
                        'function': '',
                        section: '',
                        unit: '',
                        duty_station_id: '',
                        office_location: '', // ADDED: office_location field
                        type_of_contract: '',
                        expiration_date: '',
                        supervisor: '',
                        mode_of_work: '',
                        user_id: currentUser?.id || '',
                    };
                    setFormData(initialFormData);
                    setValidationErrors(validateForm(initialFormData));
                } else {
                    setEmployment(data);
                    const newFormData = {
                        id: data.id || null,
                        'function': data.function || '',
                        section: data.section || '',
                        unit: data.unit || '',
                        duty_station_id: data.duty_station_id || '',
                        office_location: data.office_location || '', // ADDED: office_location field
                        type_of_contract: data.type_of_contract || '',
                        expiration_date: data.expiration_date || '',
                        supervisor: data.supervisor || '',
                        mode_of_work: data.mode_of_work || '',
                        user_id: currentUser?.id || '',
                    };
                    setFormData(newFormData);
                    setValidationErrors(validateForm(newFormData));

                    if (onValidationChange) {
                        onValidationChange(checkFormCompletion(newFormData));
                    }
                }
            } catch (error) {
                if (error.response?.status === 404) {
                    setEmployment(null);
                } else {
                    setError(t('error_message'));
                }
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [currentUser, t]);

    const handleEdit = () => {
        if (!employment) {
            console.error('No employment data to edit');
            return;
        }

        const editData = {
            id: employment?.id || null,
            'function': employment?.function || '',
            section: employment?.section || '',
            unit: employment?.unit || '',
            duty_station_id: employment?.duty_station_id || '',
            office_location: employment?.office_location || '', // ADDED: office_location field
            type_of_contract: employment?.type_of_contract || '',
            expiration_date: employment?.expiration_date || '',
            supervisor: employment?.supervisor || '',
            mode_of_work: employment?.mode_of_work || '',
            user_id: currentUser?.id || ''
        };

        console.log('Edit data:', editData);
        setFormData(editData);
        setValidationErrors(validateForm(editData));
        setIsEditing(true);
    };

    const handleFieldChange = (e) => {
        const { name, value } = e.target;
        const newFormData = {
            ...formData,
            [name]: value,
        };

        setFormData(newFormData);

        // Mark field as touched
        setTouchedFields(prev => ({
            ...prev,
            [name]: true
        }));

        // Validate form and update errors
        const errors = validateForm(newFormData);
        setValidationErrors(errors);

        // Update form completion status
        const isComplete = checkFormCompletion(newFormData);
        setIsFormComplete(isComplete);
        if (onValidationChange) {
            onValidationChange(isComplete);
        }
    };

    const handleSave = async () => {
        try {
            setLoading(true);
            setError(null);

            // Mark all fields as touched for validation display
            const allTouchedFields = {};
            Object.keys(formData).forEach(key => {
                allTouchedFields[key] = true;
            });
            setTouchedFields(allTouchedFields);

            const validationErrors = validateForm(formData);
            if (Object.keys(validationErrors).length > 0) {
                setValidationErrors(validationErrors);
                setError(validationErrors);
                setLoading(false);
                return;
            }

            const payload = { ...formData, user_id: currentUser.id };
            await saveEmploymentData(payload, currentUser);

            const updatedData = await fetchEmploymentData();
            setEmployment(updatedData || payload);
            setSuccess(true);
            setError(null);
            setIsEditing(false);
            setTouchedFields({});

            const latestData = updatedData || payload;
            const normalizedData = {
                ...latestData,
                'function': latestData.function || '',
                'section': latestData.section || '',
                'unit': latestData.unit || '',
                'office_location': latestData.office_location || '', // ADDED: office_location field
                'type_of_contract': latestData.type_of_contract || ''
            };

            if (onSave) {
                onSave(normalizedData);
                setTimeout(() => {
                    setSuccess(false);
                }, 3000);
                return;
            }

            setTimeout(() => {
                setSuccess(false);
            }, 3000);
        } catch (error) {
            setError({
                api: t('error_message')
            });
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        try {
            setLoading(true);
            setError(null);

            if (employment?.id) {
                await deleteEmploymentData(employment.id);
                setEmployment(null);
                if (onSave) {
                    onSave({});
                }
            }
        } catch (error) {
            setError(t('delete_error'));
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        const resetFormData = {
            'function': '',
            section: '',
            unit: '',
            duty_station_id: '',
            office_location: '', // ADDED: office_location field
            type_of_contract: '',
            expiration_date: '',
            supervisor: '',
            mode_of_work: '',
            user_id: currentUser.id,
        };
        setFormData(resetFormData);
        setValidationErrors(validateForm(resetFormData));
        setTouchedFields({});
        setIsEditing(false);
    };

    const handleAddEmployment = () => {
        const newFormData = {
            'function': '',
            section: '',
            unit: '',
            duty_station_id: '',
            office_location: '', // ADDED: office_location field
            type_of_contract: '',
            expiration_date: '',
            supervisor: '',
            mode_of_work: '',
            user_id: currentUser.id,
        };
        setFormData(newFormData);
        setValidationErrors(validateForm(newFormData));
        setTouchedFields({});
        setIsEditing(true);
    };

    const employmentFields = [
        { label: t('function_label'), value: employment?.function },
        { label: t('section_label'), value: employment?.section },
        { label: t('unit_label'), value: employment?.unit },
        { label: t('duty_station_label'), value: employment ? getDutyStationName(employment.duty_station_id) : t('form_incomplete_message') },
        { label: t('office_location_label'), value: employment?.office_location || t('form_incomplete_message') }, // ADDED: office_location field
        { label: t('type_of_contract_label'), value: employment?.type_of_contract },
        { label: t('expiration_date_label'), value: employment?.expiration_date },
        { label: t('supervisor_label'), value: employment?.supervisor || t('form_incomplete_message') },
        { label: t('mode_of_work_label'), value: employment?.mode_of_work || t('form_incomplete_message') },
    ];

    return (
        <Container className="my-5">
            <h2 className="text-center mb-4" style={{ fontWeight: 'bold', color: '#4682B4' }}>
                {t('employment_info_title')}
            </h2>

            {loading && <Alert variant="info">{t('loading_info')}</Alert>}

            {error && (
                <Alert variant="danger" onClose={() => setError(null)} dismissible>
                    {typeof error === 'object' && !error.api ? (
                        <ul className="mb-0">
                            {Object.entries(error).map(([field, message]) => (
                                <li key={field}>{message}</li>
                            ))}
                        </ul>
                    ) : (
                        error.api
                    )}
                </Alert>
            )}

            {success && (
                <Alert variant="success" onClose={() => setSuccess(false)} dismissible>
                    {t('success_message')}
                </Alert>
            )}

            {!loading && !employment && !isEditing && (
                <Alert variant="warning" className="text-center">
                    {t('no_employment_record')}
                </Alert>
            )}

            {!loading && !employment && !isEditing && (
                <div className="d-flex justify-content-center">
                    <Button variant="success" onClick={handleAddEmployment}>
                        <FaPlus className="me-2" />
                        {t('add_employment_record')}
                    </Button>
                </div>
            )}

            {!loading && employment && !isEditing && (
                <Card className="shadow-lg p-4 mb-4 bg-light rounded">
                    <div className="row">
                        {employmentFields.map((field, index) => (
                            <div key={index} className="col-md-6 mb-3">
                                <div
                                    className="p-3 rounded d-flex justify-content-between align-items-center"
                                    style={{
                                        backgroundColor: '#f8f9fa',
                                        border: '1px solid #dee2e6',
                                    }}
                                >
                                    <strong className="me-2">{field.label}:</strong>
                                    <span className="text-secondary">
                                        {field.value}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="d-flex justify-content-end mt-4">
                        <Button variant="outline-warning" onClick={handleEdit} className="me-2 shadow-sm">
                            <FaEdit className="me-1" /> {t('edit_record')}
                        </Button>
                        <Button variant="outline-danger" onClick={handleDelete} className="shadow-sm">
                            <FaTrash className="me-1" /> {t('delete_record')}
                        </Button>
                    </div>
                </Card>
            )}

            {isEditing && (
                <Card className="shadow-lg p-4 mb-4 bg-light rounded">
                    <EmploymentFormSection
                        formData={formData || {}}
                        handleChange={handleFieldChange}
                        errors={validationErrors || {}}
                        touchedFields={touchedFields}
                        requiredFields={requiredFields}
                    />
                    <div className="d-flex justify-content-end mt-4">
                        <Button variant="success" onClick={handleSave} className="me-2 shadow-sm">
                            <FaCheck className="me-1" /> {t('save_record')}
                        </Button>
                        <Button variant="secondary" onClick={handleCancel} className="shadow-sm">
                            <FaTimes className="me-1" /> {t('cancel_action')}
                        </Button>
                    </div>
                </Card>
            )}
        </Container>
    );
};

export default EmploymentForm;